export interface AgentItem {
  id: number;
  agent_name: string;
}

export interface SchemaField {
  name: string;
  type: "string" | "number" | "boolean" | "array" | "object";
  description: string;
}

export type ArgBindingValueSource = "variable" | "static";

/** Maps a workflow input field to either a prior variable's fill value or a fixed static value. */
export interface ComputedArgBinding {
  workflow_arg: string;
  value_source: ArgBindingValueSource;
  /** When value_source is "variable", name of the placeholder whose fill is passed. */
  variable: string;
  /** When value_source is "static", literal value (JSON primitives/objects parse when sent to the workflow). */
  static_value: string;
}

export interface ComputedPlaceholder {
  placeholder: string;
  workflow_ref: string;
  /** @deprecated Prefer `arg_bindings` — when only this is set, each entry is both workflow key and variable name. */
  args?: string[];
  arg_bindings?: ComputedArgBinding[];
  value_path: string;
}

export type VariableSource = "seed" | "computed";

export interface GenerationVariable {
  name: string;
  description: string;
  /** Example value for this placeholder; used to build an example user message from the template. */
  example: string;
  source: VariableSource;
  workflow_ref: string;
  arg_bindings: ComputedArgBinding[];
  value_path: string;
}

export function normalizeVariableArgBindings(v: {
  arg_bindings?: ComputedArgBinding[];
  args?: string[];
}): ComputedArgBinding[] {
  const fromBindings = v.arg_bindings;
  if (Array.isArray(fromBindings) && fromBindings.length > 0) {
    const out: ComputedArgBinding[] = [];
    for (const b of fromBindings) {
      if (!b || typeof b !== "object") continue;
      const wa = String(b.workflow_arg || "").trim();
      if (!wa) continue;
      const src = b.value_source === "static" ? "static" : "variable";
      if (src === "static") {
        out.push({
          workflow_arg: wa,
          value_source: "static",
          variable: "",
          static_value: String((b as ComputedArgBinding).static_value ?? ""),
        });
        continue;
      }
      const variable = String(b.variable || "").trim();
      if (!variable) continue;
      out.push({
        workflow_arg: wa,
        value_source: "variable",
        variable,
        static_value: "",
      });
    }
    return out;
  }
  const legacy = v.args || [];
  return legacy
    .filter(Boolean)
    .map((variable) => ({
      workflow_arg: String(variable).trim(),
      value_source: "variable" as const,
      variable: String(variable).trim(),
      static_value: "",
    }));
}

export function normalizeGenerationVariableFromMeta(raw: Partial<GenerationVariable> & { args?: string[] }): GenerationVariable {
  const base: GenerationVariable = {
    name: raw.name || "",
    description: raw.description || "",
    example: raw.example || "",
    source: raw.source === "computed" ? "computed" : "seed",
    workflow_ref: raw.workflow_ref || "",
    value_path: raw.value_path || "",
    arg_bindings: normalizeVariableArgBindings(raw as GenerationVariable),
  };
  return base;
}

export type GenerationMode = "open_ended" | "structured";
export type TeacherType = "model" | "workflow" | "agent";

export interface GenerationConfig {
  generation_mode: GenerationMode;
  generation_teacher_type: TeacherType;
  generation_model: string;
  generation_seed_model: string;
  generation_size: number;
  generation_size_tier: string;
  generation_max_tokens: number | null;
  generation_domains: string[];
  generation_context: string;
  generation_include_reasoning: boolean;
  generation_response_instructions: string;
  /** System message stored on each generated example; not sent to the teacher. */
  generation_request_system_prompt: string;
  /** Model teacher only: system prompt used when calling the teacher for responses. */
  generation_teacher_system_prompt: string;
  generation_user_template: string;
  generation_user_example: string;
  generation_response_example: string;
  generation_difficulty_cues: string;
  generation_seed_instructions: string;
  generation_workflow_input: string;
  /** Dot path into teacher workflow JSON output used as the training assistant message (workflow teacher only). */
  generation_teacher_response_value_path: string;
  generation_response_schema: SchemaField[];
  generation_computed_placeholders: ComputedPlaceholder[];
  generation_variables?: GenerationVariable[];
  generation_use_prod_seeds: boolean;
  generation_prod_seed_ids: string[];
}

export const GENERATION_SIZE_TIERS = [
  { id: "small", label: "Small", count: 500, description: "Good for narrow, focused domains" },
  { id: "medium", label: "Medium", count: 2000, description: "Solid for single-domain fine-tuning" },
  { id: "large", label: "Large", count: 10000, description: "Broad coverage or high-stakes use cases" },
  { id: "custom", label: "Custom", count: 0, description: "Specify your own count" },
];

export const GENERATION_DOMAINS = [
  { id: "code_generation", label: "Code Generation", description: "Writing, debugging, and reviewing code" },
  { id: "mathematical_reasoning", label: "Mathematical Reasoning", description: "Arithmetic, algebra, proofs, and problem solving" },
  { id: "logical_reasoning", label: "Logical Reasoning", description: "Deduction, induction, and critical thinking" },
  { id: "summarization", label: "Summarization", description: "Condensing text while preserving key information" },
  { id: "classification", label: "Classification", description: "Categorizing inputs into predefined classes" },
  { id: "creative_writing", label: "Creative Writing", description: "Storytelling, poetry, and creative content" },
  { id: "instruction_following", label: "Instruction Following", description: "Accurately executing multi-step instructions" },
  { id: "question_answering", label: "Question Answering", description: "Answering questions from context or knowledge" },
  { id: "data_extraction", label: "Data Extraction", description: "Extracting structured data from unstructured text" },
  { id: "conversation", label: "Conversation", description: "Multi-turn dialogue and chat interactions" },
];

export const SCHEMA_FIELD_TYPES = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "array", label: "Array" },
  { value: "object", label: "Object" },
] as const;

export const MODEL_PRICING: Record<string, { inputPerMToken: number; outputPerMToken: number }> = {
  "anthropic:claude-sonnet-4-20250514": { inputPerMToken: 3, outputPerMToken: 15 },
  "anthropic:claude-opus-4-20250514": { inputPerMToken: 15, outputPerMToken: 75 },
  "openai:gpt-4o": { inputPerMToken: 2.5, outputPerMToken: 10 },
  "openai:gpt-4o-mini": { inputPerMToken: 0.15, outputPerMToken: 0.6 },
  "openai:gpt-4.1": { inputPerMToken: 2, outputPerMToken: 8 },
  "openai:gpt-4.1-mini": { inputPerMToken: 0.4, outputPerMToken: 1.6 },
  "openai:gpt-4.1-nano": { inputPerMToken: 0.1, outputPerMToken: 0.4 },
  "google:gemini-2.5-pro": { inputPerMToken: 1.25, outputPerMToken: 10 },
  "google:gemini-2.5-flash": { inputPerMToken: 0.15, outputPerMToken: 0.6 },
};

export const DEFAULT_PRICING = { inputPerMToken: 3, outputPerMToken: 15 };
export const AVG_INPUT_TOKENS_PER_EXAMPLE = 250;
export const AVG_OUTPUT_TOKENS_NO_REASONING = 500;
export const AVG_OUTPUT_TOKENS_WITH_REASONING = 2500;

export function extractPlaceholders(template: string): string[] {
  const matches = template.match(/\{\{(\w+)\}\}/g) || [];
  const names = matches.map((m) => m.replace(/\{\{|\}\}/g, ""));
  return names.filter((v, i, a) => a.indexOf(v) === i);
}

/** Same substitution rules as dataset generation backend: examples by variable name, else `(example name)`. */
export function exampleFilledUserMessageFromVariables(userTemplate: string, variables: GenerationVariable[]): string {
  const tpl = userTemplate || "";
  if (!tpl.trim()) return "";
  const examples: Record<string, string> = {};
  for (const v of variables) {
    const n = v.name.trim();
    if (!n) continue;
    examples[n] = (v.example || "").trim();
  }
  return tpl.replace(/\{\{(\w+)\}\}/g, (_m, name: string) => {
    const ex = examples[name];
    if (ex) return ex;
    return `(example ${name})`;
  });
}

/**
 * Placeholders that exist in the configured set (e.g. from Variables + templates + computed args)
 * but do not appear in the user message template. They may still be used in the system prompt or workflows.
 */
export function placeholdersUnusedInUserTemplate(
  allPlaceholderNames: string[],
  userTemplate: string
): string[] {
  const usedInUser = new Set(extractPlaceholders(userTemplate || ""));
  return allPlaceholderNames.filter((p) => !usedInUser.has(p));
}

/**
 * All placeholder names that need values at generation time: from user/system templates plus
 * every `args` entry on computed-placeholder rows (workflow inputs that must be filled before injection).
 */
export function computedPlaceholdersFromVariables(vars: GenerationVariable[]): ComputedPlaceholder[] {
  const out: ComputedPlaceholder[] = [];
  for (const v of vars) {
    if (v.source !== "computed") continue;
    const name = v.name.trim();
    if (!name) continue;
    const bindings = normalizeVariableArgBindings(v);
    out.push({
      placeholder: name,
      workflow_ref: v.workflow_ref || "",
      args: bindings.filter((b) => b.value_source === "variable").map((b) => b.variable),
      arg_bindings: bindings,
      value_path: v.value_path || "",
    });
  }
  return out;
}

export function mergePlaceholdersWithGenerationVars(
  systemPrompt: string,
  userTemplate: string,
  variables: GenerationVariable[] | undefined,
  computedFallback: ComputedPlaceholder[] | undefined
): string[] {
  if (variables && variables.length > 0) {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of variables) {
      const n = v.name.trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    const fromTpl = [
      ...extractPlaceholders(systemPrompt || ""),
      ...extractPlaceholders(userTemplate || ""),
    ];
    for (const p of fromTpl) {
      if (!seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }
  return mergePlaceholdersWithComputedArgs(systemPrompt, userTemplate, computedFallback);
}

export function mergePlaceholdersWithComputedArgs(
  systemPrompt: string,
  userTemplate: string,
  computedPlaceholders: ComputedPlaceholder[] | undefined
): string[] {
  const fromTemplates = [
    ...extractPlaceholders(systemPrompt || ""),
    ...extractPlaceholders(userTemplate || ""),
  ];
  const fromArgs: string[] = [];
  for (const row of computedPlaceholders || []) {
    if (row.arg_bindings && row.arg_bindings.length > 0) {
      for (const b of row.arg_bindings) {
        if ((b as ComputedArgBinding).value_source === "static") continue;
        const name = String(b.variable || "").trim();
        if (name) fromArgs.push(name);
      }
    } else {
      for (const arg of row.args || []) {
        const name = String(arg).trim();
        if (name) fromArgs.push(name);
      }
    }
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of [...fromTemplates, ...fromArgs]) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

/** Counts for /datasets/generate/estimate_cost (mirrors backend structured_placeholder_plan). */
export function getGenerationCostDimensions(
  config: Pick<
    GenerationConfig,
    | "generation_request_system_prompt"
    | "generation_user_template"
    | "generation_variables"
    | "generation_computed_placeholders"
  >
): {
  num_seed_variables: number;
  num_computed_variables: number;
  sequential_placeholders: boolean;
} {
  const vars = config.generation_variables || [];
  const sequentialPlaceholders = vars.length > 0;

  const compVars = computedPlaceholdersFromVariables(vars);
  const legacy = config.generation_computed_placeholders || [];
  const computedNames = new Set<string>();
  for (const c of compVars) {
    const p = String(c.placeholder || "").trim();
    if (p) computedNames.add(p);
  }
  for (const c of legacy) {
    const p = String(c.placeholder || "").trim();
    if (p) computedNames.add(p);
  }

  const allPh = mergePlaceholdersWithGenerationVars(
    config.generation_request_system_prompt || "",
    config.generation_user_template || "",
    sequentialPlaceholders ? vars : undefined,
    sequentialPlaceholders ? undefined : legacy.length > 0 ? legacy : undefined
  );
  const numSeedVariables = allPh.filter((n) => !computedNames.has(n)).length;
  const numComputedVariables = computedNames.size;

  return {
    num_seed_variables: numSeedVariables,
    num_computed_variables: numComputedVariables,
    sequential_placeholders: sequentialPlaceholders,
  };
}

/** Review step / API: dimensions from persisted dataset meta. */
export function getGenerationCostDimensionsFromMeta(
  meta: Record<string, unknown>
): ReturnType<typeof getGenerationCostDimensions> {
  const vars = Array.isArray(meta.generation_variables)
    ? (meta.generation_variables as GenerationVariable[])
    : [];
  const comp = Array.isArray(meta.generation_computed_placeholders)
    ? (meta.generation_computed_placeholders as ComputedPlaceholder[])
    : [];
  return getGenerationCostDimensions({
    generation_request_system_prompt: String(meta.generation_request_system_prompt || ""),
    generation_user_template: String(meta.generation_user_template || ""),
    generation_variables: vars,
    generation_computed_placeholders: comp,
  });
}

export function buildDefaultWorkflowInput(placeholders: string[]): string {
  if (placeholders.length === 0) return "{}";
  const obj: Record<string, string> = {};
  for (const p of placeholders) obj[p] = `{{${p}}}`;
  return JSON.stringify(obj, null, 2);
}

/** Resolve workflow `meta.input_schema` for the selected teacher workflow ref. */
export function getTeacherWorkflowInputSchema(
  workflows: { workflow_ref_name: string; meta?: { input_schema?: unknown } }[],
  workflowRef: string
): Record<string, unknown> | undefined {
  const wf = workflows.find((w) => w.workflow_ref_name === workflowRef);
  const s = wf?.meta?.input_schema;
  if (s && typeof s === "object" && !Array.isArray(s)) {
    return s as Record<string, unknown>;
  }
  return undefined;
}

/**
 * Teacher workflow `ctx.input` JSON template.
 * When the workflow defines `input_schema`, keys match that schema only (values default to `{{fieldName}}`).
 * With no schema, falls back to one key per prompt/computed placeholder.
 */
export function buildMergedTeacherWorkflowInput(
  placeholders: string[],
  inputSchema: Record<string, unknown> | null | undefined
): string {
  const schemaKeys =
    inputSchema && typeof inputSchema === "object" && !Array.isArray(inputSchema)
      ? Object.keys(inputSchema)
      : [];

  if (schemaKeys.length > 0) {
    const obj: Record<string, string> = {};
    for (const k of schemaKeys) {
      obj[k] = `{{${k}}}`;
    }
    return JSON.stringify(obj, null, 2);
  }

  return buildDefaultWorkflowInput(placeholders);
}

/**
 * When variables or schema fields are added, add missing keys to teacher workflow JSON without removing user edits.
 * If `schemaKeys` is set (workflow has input_schema), only those keys are auto-added; otherwise uses `placeholders`.
 */
export function mergeWorkflowInputMissingVariableKeys(
  currentJson: string | undefined,
  placeholders: string[],
  schemaKeys?: string[]
): string {
  let obj: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse((currentJson || "{}").trim() || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      obj = { ...parsed };
    }
  } catch {
    obj = {};
  }
  const keysToEnsure =
    schemaKeys !== undefined && schemaKeys.length > 0 ? schemaKeys : placeholders;
  for (const k of keysToEnsure) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) {
      obj[k] = `{{${k}}}`;
    }
  }
  return JSON.stringify(obj, null, 2);
}

export function generateSchemaPreview(fields: SchemaField[]): string {
  const named = fields.filter((f) => f.name);
  if (named.length === 0) return "";
  const lines = named.map((f) => {
    const entry = `  "${f.name}": ${f.type}`;
    return f.description ? `${entry}  // ${f.description}` : entry;
  });
  return "{\n" + lines.join(",\n") + "\n}";
}

export function generateExamplePlaceholder(fields: SchemaField[]): string {
  const named = fields.filter((f) => f.name);
  if (named.length === 0) return "";
  const obj: Record<string, unknown> = {};
  for (const field of named) {
    switch (field.type) {
      case "string": obj[field.name] = field.description || "..."; break;
      case "number": obj[field.name] = 0; break;
      case "boolean": obj[field.name] = true; break;
      case "array": obj[field.name] = []; break;
      case "object": obj[field.name] = {}; break;
    }
  }
  return JSON.stringify(obj, null, 2);
}

export function estimateCost(
  model: string,
  size: number,
  includeReasoning: boolean
): { inputTokens: number; outputTokens: number; costUsd: number; isEstimatedPricing: boolean } | null {
  if (!model || size <= 0) return null;
  const pricing = MODEL_PRICING[model] || DEFAULT_PRICING;
  const isEstimatedPricing = !MODEL_PRICING[model];
  const inputTokens = size * AVG_INPUT_TOKENS_PER_EXAMPLE;
  const outputTokens = size * (includeReasoning ? AVG_OUTPUT_TOKENS_WITH_REASONING : AVG_OUTPUT_TOKENS_NO_REASONING);
  const costUsd = (inputTokens / 1_000_000) * pricing.inputPerMToken + (outputTokens / 1_000_000) * pricing.outputPerMToken;
  return { inputTokens, outputTokens, costUsd, isEstimatedPricing };
}

export function formatTokenCount(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
  return tokens.toString();
}
